import _ from 'lodash'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Card, Grid, Stack, Step, StepConnector, StepContent, StepLabel, Stepper, styled, Typography } from '@mui/material'

import colors from '@/assets/scss/_themes-vars.module.scss'
import { Expertise } from '@/core/models/expertise.model'

import Documents from '@/pages/expertise/components/Documents'
import InitiatorInfos from '@/pages/expertise/components/InitiatorInfo'
import HealthDataList from '@/pages/expertise/containers/HealthDataList'
import PatientInfosCard from '@/pages/expertise/components/PatientInfosCard'
import ExpertiseForms from '@/pages/expertise/containers/ExpertiseForms'

const CustomStepLabel = styled(StepLabel)({
  padding: 0,
})

const CustomStepContent = styled(StepContent)(({ theme }) => ({
  borderLeftWidth: '2px',
  borderLeftColor: theme.palette.primary.light,
  marginLeft: '11px',
  paddingTop: '8px',
}))

const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
  marginLeft: '11px',
  '& .MuiStepConnector-line': {
    borderLeftWidth: '2px',
    borderLeftColor: theme.palette.primary.light,
  },
}))

export function Summary({ expertise, service }: { expertise: Expertise; service: string }) {
  const { t } = useTranslation()

  const documents = useMemo(() => expertise.files.map((file) => ({ filename: file.filename, key: file.key, url: file.url })), [expertise])

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" p={3}>
        <Stack spacing={1} sx={{ flex: 1 }}>
          <Typography variant="h2" component="div" noWrap sx={{ fontSize: { sm: '20px', lg: '24px' }, lineHeight: '24px', fontWeight: 700 }}>
            {t('page.expertiseValidation.title')}
          </Typography>
          <Typography
            variant="subtitle1"
            component="div"
            sx={{ color: colors.gray500, fontSize: { sm: '16px', lg: '18px' }, lineHeight: '20px', fontWeight: 400 }}
          >
            {t('page.expertiseValidation.subtitle', { service: _.capitalize(service) })}
          </Typography>
        </Stack>
      </Stack>
      <Box sx={{ flex: '1 1 auto', pl: 3, pr: 3, pb: 2, overflowY: 'auto' }}>
        <Grid container flexDirection={{ xs: 'column-reverse', sm: 'row' }} spacing={5}>
          <Grid item xs={12} sm={7} md={8} xl={9}>
            <Box sx={{ width: '100%', pt: '10px', pb: '20px' }}>
              <Stepper activeStep={-1} orientation="vertical" connector={<CustomStepConnector />} sx={{ flex: 1 }}>
                <Step active disabled={false} expanded>
                  <CustomStepLabel>{t('page.expertiseValidation.initiatorInformation.title')}</CustomStepLabel>
                  <CustomStepContent>
                    <InitiatorInfos who="initiator" staff={expertise.createdBy} />
                  </CustomStepContent>
                </Step>
                <Step active disabled={false} expanded>
                  <CustomStepLabel>{t('page.expertiseValidation.healthData.title')}</CustomStepLabel>
                  <CustomStepContent>
                    <HealthDataList readOnly showTitle={false} />
                  </CustomStepContent>
                </Step>
                <Step active disabled={false} expanded>
                  <CustomStepLabel>{t('page.expertiseValidation.documents.title')}</CustomStepLabel>
                  <CustomStepContent>
                    <Documents documents={documents} readOnly showTitle={false} />
                  </CustomStepContent>
                </Step>
                <Step active disabled={false} expanded>
                  <CustomStepLabel>{t('page.expertiseValidation.forms.title')}</CustomStepLabel>
                  <CustomStepContent>
                    <ExpertiseForms expertiseId={expertise._id} readOnly showTitle={false} />
                  </CustomStepContent>
                </Step>
                <Step active disabled={false} expanded>
                  <CustomStepLabel>{t('page.expertiseValidation.initialMessage.title')}</CustomStepLabel>
                  <CustomStepContent>
                    <Card
                      sx={{
                        padding: 3,
                        borderRadius: '8px',
                        border: `1px solid ${colors.gray200}`,
                        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
                      }}
                      elevation={0}
                    >
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                        {expertise.comment}
                      </Typography>
                    </Card>
                  </CustomStepContent>
                </Step>
              </Stepper>
            </Box>
          </Grid>
          <Grid item xs={12} sm={5} md={4} xl={3}>
            <Box sx={{ position: 'sticky', top: 0, pt: '10px' }}>
              <PatientInfosCard patient={expertise.patient} />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}
